import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { useMarket } from '@/lib/market/market-context';
import { MARKETS, type Market } from '@/lib/market/types';

import { SegmentedControl } from './SegmentedControl';

/**
 * §10.1：市場是**一個地方**驅動四件事的那個開關。
 *
 * P0 時它只改 context。P1 有了路由樹之後，**市場是路徑區段**（`/:market/...`），
 * 所以權威變成 URL：這裡改的是網址，MarketProvider 再由 `$market/route.tsx`
 * 同步過去。dataset.market 仍然只有 MarketProvider 一個寫入點。
 *
 * 切換市場一律導向該市場的「今日重點」（`/:market/dashboard`），不帶代號與其他頁面狀態：
 * 代號是分市場的，把台股的 2330 帶去美股只會得到「找不到」。只保留時光機的 `?as_of`。
 */
export function MarketSwitcher() {
  const { t } = useTranslation();
  const { market } = useMarket();
  const navigate = useNavigate();

  const options = MARKETS.map((value: Market) => ({
    value,
    label: t(value === 'tw' ? 'market.tw' : 'market.us'),
  }));

  return (
    <SegmentedControl
      legend={t('market.label')}
      value={market}
      options={options}
      onChange={(next) => {
        // 切換市場一律回到該市場的「今日重點」：代號是分市場的，帶著台股的 2330 去美股
        // 只會得到「找不到」。只保留時光機的 as_of，不帶 symbol 與其他頁面狀態。
        void navigate({
          to: '/$market/dashboard',
          params: { market: next },
          search: (prev: Record<string, unknown>) =>
            typeof prev['as_of'] === 'string' ? { as_of: prev['as_of'] } : {},
        });
      }}
    />
  );
}
